import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useStore, formatPrice } from "../context/StoreContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderConfirmationPage = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const { currency } = useStore();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("Receipt link expired — look it up on Track Order");
      return;
    }
    axios
      .get(`${API}/order/${encodeURIComponent(id)}?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setOrder(data))
      .catch((err) => {
        setError(err?.response?.data?.detail || "Order not found");
      });
  }, [id, token]);

  if (error) {
    return (
      <div data-testid="page-order-confirmation-error" className="pt-[140px] pb-20 text-center min-h-[60vh] px-4">
        <p className="font-mono text-xs uppercase tracking-widest text-dc-red mb-3">
          {error.toUpperCase()}
        </p>
        <h1 className="font-display uppercase text-5xl md:text-7xl">CAN'T FIND THAT ORDER</h1>
        <Link to="/" className="inline-block mt-6 btn-solid">Back home</Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="pt-[140px] pb-20 text-center min-h-[60vh] px-4">
        <p className="font-mono text-xs uppercase tracking-widest text-ink/60">LOADING ORDER...</p>
      </div>
    );
  }

  const addr = order.shipping_address || {};

  return (
    <div data-testid="page-order-confirmation" className="pt-[100px] pb-24">
      <div className="max-w-[900px] mx-auto px-4 md:px-12">
        <p className="font-mono text-[11px] uppercase tracking-widest text-dc-red mb-4">
          PAYMENT CONFIRMED / {order.id}
        </p>
        <h1 className="font-display uppercase text-5xl sm:text-7xl md:text-8xl leading-none">
          You've damaged the culture.
        </h1>
        <p className="font-body text-lg md:text-xl mt-5 text-ink/85 max-w-2xl">
          Your order is in. Each piece is made and shipped by hand, so allow us the time it takes. You'll get an email when it leaves the workshop.
        </p>

        <section className="mt-12 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-ink/60 mb-3">
              SHIPPING TO
            </p>
            <p className="font-body text-lg leading-relaxed">
              {order.customer_name}<br />
              {addr.address}{addr.apartment ? `, ${addr.apartment}` : ""}<br />
              {addr.city}, {addr.state} {addr.pin}<br />
              {addr.country || "India"} / {addr.phone}<br />
              <span className="font-mono text-xs uppercase tracking-widest text-ink/60">{order.customer_email}</span>
            </p>
          </div>
          <div className="card-surface rounded-[20px] shadow-card p-5 md:p-6 min-w-[260px]">
            <p className="font-mono text-[11px] uppercase tracking-widest text-ink/60 mb-3">
              ESTIMATED DELIVERY
            </p>
            <p className="font-display uppercase text-2xl">7 to 14 days</p>
            <p className="font-mono text-[11px] uppercase tracking-widest text-ink/60 mt-4 mb-2">STATUS</p>
            <p className="font-mono text-sm uppercase tracking-widest">{order.status}</p>
          </div>
        </section>

        <section className="mt-12">
          <p className="font-mono text-[11px] uppercase tracking-widest text-ink/60 mb-4">
            WHAT YOU GOT
          </p>
          <div className="border-t border-ink/12">
            {(order.line_items || []).map((li, i) => (
              <div key={`${li.product_id}-${li.size}-${i}`} className="grid grid-cols-[1fr_auto] gap-4 py-4 border-b border-ink/12">
                <div>
                  <p className="font-body text-lg">{li.name}</p>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-ink/60 mt-1">
                    SIZE {li.size} / x{li.qty}
                  </p>
                </div>
                <p className="font-mono text-sm self-center">
                  {formatPrice(li.price_inr * li.qty, currency)}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-5 font-mono text-sm space-y-2 max-w-xs ml-auto">
            <div className="flex justify-between">
              <span className="uppercase text-xs tracking-widest text-ink/70">Subtotal</span>
              <span>{formatPrice(order.subtotal, currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="uppercase text-xs tracking-widest text-ink/70">GST</span>
              <span>{formatPrice(order.tax, currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="uppercase text-xs tracking-widest text-ink/70">Shipping</span>
              <span>{order.shipping === 0 ? "FREE" : formatPrice(order.shipping, currency)}</span>
            </div>
            <div className="flex justify-between font-display uppercase text-xl pt-2 border-t border-ink/15">
              <span>Total</span>
              <span>{formatPrice(order.total, currency)}</span>
            </div>
          </div>
        </section>

        <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/shop" className="btn-ghost" data-testid="confirmation-continue-shopping">
            Continue shopping
          </Link>
          <Link
            to={`/track-order?order=${encodeURIComponent(order.id)}&email=${encodeURIComponent(order.customer_email || "")}`}
            className="btn-solid"
            data-testid="confirmation-track"
          >
            Track this order
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
